import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GridApi } from 'ag-grid-community';
import { ChevronsDownUp, ChevronsUpDown, Download, EyeOff, ListCollapse, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import type { EngineRow, SheetId } from '@/features/projections/engine';
import {
  PNL_SECTION_LABEL,
  CASH_SECTION_LABEL,
  sectionIsCollapsible,
} from '@/features/projections/engine';
import type { ProjectionModel } from '@/features/projections/useProjectionModel';
import {
  useDeleteSheetRow,
  useSetAccountOverride,
  useSetCellStyle,
  useUpsertSheetRow,
} from '@/features/projections/workbookHooks';
import {
  ProjectionsGrid,
  type ColumnMode,
  type GridActions,
  type ViewMode,
} from './ProjectionsGrid';
import type { RowDensity } from '@/features/projections/density';

export interface FocusRequest {
  rowKey: string | null;
  month: string | null;
  nonce: number;
}

export interface SheetTabProps {
  sheet: SheetId;
  pm: ProjectionModel;
  zoom: number;
  columnMode: ColumnMode;
  density: RowDensity;
  /** Height of the grid viewport in px. */
  height: number;
  /** Scroll to + flash a cell (used by Insights → "Open on Main sheet"). */
  focusRequest?: FocusRequest | null;
}

const ADDABLE_SECTIONS: Array<{ id: string; label: string }> = [
  ...(['income', 'cogs', 'expense', 'other-income', 'other-expense'] as const).map((s) => ({
    id: s,
    label: PNL_SECTION_LABEL[s],
  })),
  ...(['operating', 'investing', 'financing'] as const).map((s) => ({
    id: s,
    label: CASH_SECTION_LABEL[s],
  })),
];

/**
 * One spreadsheet tab (Main / P&L / Balance sheet / Cash flow). Owns the
 * per-sheet toolbar and wires the grid's row actions to the workbook
 * tables; every number comes from the shared `ProjectionModel`.
 */
const collapsedKey = (sheet: SheetId) => `atlas:projections:collapsed:${sheet}`;

function loadCollapsed(sheet: SheetId): Set<string> {
  try {
    const raw = window.localStorage.getItem(collapsedKey(sheet));
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((k): k is string => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

export function SheetTab({
  sheet,
  pm,
  zoom,
  columnMode,
  density,
  height,
  focusRequest,
}: SheetTabProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('amount');
  const [showAll, setShowAll] = useState(false);
  const apiRef = useRef<GridApi<EngineRow> | null>(null);

  // Folded sections persist per sheet so the operator's reading layout
  // (e.g. "just show me section totals") survives a reload.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() =>
    loadCollapsed(sheet),
  );
  useEffect(() => {
    try {
      window.localStorage.setItem(collapsedKey(sheet), JSON.stringify([...collapsedSections]));
    } catch {
      // storage unavailable — fine
    }
  }, [sheet, collapsedSections]);
  const toggleSection = useCallback((key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!focusRequest) return;
    const api = apiRef.current;
    if (!api) return;
    const t = window.setTimeout(() => {
      const colId = focusRequest.month
        ? `${columnMode === 'stacked' ? 'p:' : 'a:'}${focusRequest.month}`
        : 'label';
      let rowIndex: number | null = null;
      if (focusRequest.rowKey) {
        const node = api.getRowNode(focusRequest.rowKey);
        rowIndex = node?.rowIndex ?? null;
      }
      if (rowIndex !== null) api.ensureIndexVisible(rowIndex, 'middle');
      if (focusRequest.month) api.ensureColumnVisible(colId, 'middle');
      if (rowIndex !== null) {
        api.setFocusedCell(rowIndex, colId);
        const node = api.getDisplayedRowAtIndex(rowIndex);
        if (node) api.flashCells({ rowNodes: [node], columns: [colId], flashDuration: 1500 });
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [focusRequest, columnMode]);

  const upsertRow = useUpsertSheetRow(pm.scenario);
  const deleteRow = useDeleteSheetRow(pm.scenario);
  const setOverride = useSetAccountOverride(pm.scenario);
  const setStyle = useSetCellStyle(pm.scenario);

  const rows = useMemo(() => pm.buildRows(sheet, showAll), [pm, sheet, showAll]);

  const sections = useMemo(
    () =>
      rows
        .filter((r) => r.kind === 'section' && sectionIsCollapsible(rows, r.key))
        .map((r) => ({ key: r.key, label: r.label })),
    [rows],
  );
  const allCollapsed = sections.length > 0 && sections.every((s) => collapsedSections.has(s.key));

  const editableSheet = sheet === 'main' || sheet === 'pnl' || sheet === 'sales';

  const hiddenAccounts = useMemo(
    () =>
      pm.sheetRows
        .filter((r) => r.kind === 'account' && r.hidden && r.account_id)
        .map((r) => ({
          rowId: r.id,
          accountId: r.account_id!,
          name: r.label ?? pm.accounts.find((a) => a.id === r.account_id)?.name ?? 'Account',
        })),
    [pm.sheetRows, pm.accounts],
  );

  const unusedAccounts = useMemo(() => {
    if (!pm.model) return [];
    const shown = new Set(rows.filter((r) => r.accountId).map((r) => r.accountId!));
    const hidden = new Set(hiddenAccounts.map((h) => h.accountId));
    const types =
      sheet === 'pnl'
        ? new Set(['revenue', 'expense'])
        : sheet === 'main'
          ? new Set(['revenue', 'expense', 'asset', 'liability', 'equity'])
          : new Set<string>();
    return pm.accounts
      .filter(
        (a) => a.is_active && types.has(a.account_type) && !shown.has(a.id) && !hidden.has(a.id),
      )
      .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '') || a.name.localeCompare(b.name));
  }, [pm.model, pm.accounts, rows, hiddenAccounts, sheet]);

  const onError = (err: unknown) =>
    toast.error(err instanceof Error ? err.message : 'Could not update the sheet');

  const actions = useMemo<GridActions | undefined>(() => {
    if (!editableSheet) return undefined;
    return {
      insertLine: ({ afterKey, section, label }) =>
        upsertRow.mutate(
          {
            kind: 'custom',
            sheet: 'main',
            section,
            after_key: afterKey,
            label,
            value_type: 'currency',
          },
          { onError, onSuccess: () => toast.success('Line added — right-click it to rename') },
        ),
      renameRow: (row, label) => {
        if (row.kind === 'custom' && row.rowId) {
          upsertRow.mutate({ kind: 'custom', id: row.rowId, label }, { onError });
        } else if (row.accountId) {
          setOverride.mutate({ account_id: row.accountId, label }, { onError });
        }
      },
      deleteCustomRow: (rowId) => deleteRow.mutate(rowId, { onError }),
      hideAccount: (accountId) =>
        setOverride.mutate(
          { account_id: accountId, hidden: true },
          {
            onError,
            onSuccess: () =>
              toast.success('Account hidden. Its numbers still roll into totals.', {
                action: {
                  label: 'Undo',
                  onClick: () => setOverride.mutate({ account_id: accountId, hidden: false }),
                },
              }),
          },
        ),
      toggleRollup: (rowId) => {
        const current = pm.sheetRows.find((r) => r.id === rowId);
        if (!current) return;
        upsertRow.mutate(
          { kind: 'custom', id: rowId, rollup: current.rollup === 'memo' ? 'plan' : 'memo' },
          { onError },
        );
      },
      setValueType: (rowId, valueType) =>
        upsertRow.mutate({ kind: 'custom', id: rowId, value_type: valueType }, { onError }),
    };
  }, [editableSheet, upsertRow, deleteRow, setOverride, pm.sheetRows]);

  const addLineAtEnd = useCallback(
    (section: string) =>
      upsertRow.mutate(
        {
          kind: 'custom',
          sheet: 'main',
          section,
          after_key: null,
          label: 'New line',
          value_type: 'currency',
        },
        { onError, onSuccess: () => toast.success('Line added at the end of the section') },
      ),
    [upsertRow],
  );

  const exportCsv = () => {
    apiRef.current?.exportDataAsCsv({
      fileName: `projections-${sheet}-${pm.scenario}-${new Date().toISOString().slice(0, 10)}.csv`,
      processCellCallback: (p) => {
        const v = p.value;
        if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
        return v == null ? '' : String(v);
      },
    });
  };

  if (pm.isLoading || !pm.model) {
    return <Skeleton className="h-[480px] w-full" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="amount">Dollars</SelectItem>
              <SelectItem value="pct-revenue">% of revenue</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 pl-1">
            <Switch id={`show-all-${sheet}`} checked={showAll} onCheckedChange={setShowAll} />
            <Label htmlFor={`show-all-${sheet}`} className="text-xs text-muted-foreground">
              Show every account
            </Label>
          </div>
          {pm.model.hiddenWithActivity > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-900">
              {pm.model.hiddenWithActivity} hidden account
              {pm.model.hiddenWithActivity === 1 ? '' : 's'} with activity (still in totals)
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sections.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ListCollapse className="h-3.5 w-3.5" /> Sections
                  {collapsedSections.size > 0 && (
                    <span className="ml-1 rounded-full bg-secondary px-1.5 text-[10px] tabular-nums text-muted-foreground">
                      {sections.filter((s) => collapsedSections.has(s.key)).length} folded
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem
                  onSelect={() =>
                    setCollapsedSections(
                      allCollapsed ? new Set() : new Set(sections.map((s) => s.key)),
                    )
                  }
                >
                  {allCollapsed ? (
                    <>
                      <ChevronsUpDown className="h-3.5 w-3.5" /> Expand all sections
                    </>
                  ) : (
                    <>
                      <ChevronsDownUp className="h-3.5 w-3.5" /> Collapse all to totals
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Expanded sections</DropdownMenuLabel>
                {sections.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s.key}
                    checked={!collapsedSections.has(s.key)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => toggleSection(s.key)}
                  >
                    <span className="truncate">{s.label}</span>
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <p className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
                  Tip: click any section header in the sheet to fold or unfold it. Folded sections
                  keep their total lines.
                </p>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {editableSheet && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-3.5 w-3.5" /> Add line
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Blank line at the end of…</DropdownMenuLabel>
                {ADDABLE_SECTIONS.filter((s) =>
                  sheet === 'pnl' ? !['operating', 'investing', 'financing'].includes(s.id) : true,
                ).map((s) => (
                  <DropdownMenuItem key={s.id} onSelect={() => addLineAtEnd(s.id)}>
                    {s.label}
                  </DropdownMenuItem>
                ))}
                {unusedAccounts.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>QuickBooks account not shown yet</DropdownMenuLabel>
                    <div className="max-h-56 overflow-y-auto">
                      {unusedAccounts.map((a) => (
                        <DropdownMenuItem
                          key={a.id}
                          onSelect={() =>
                            setOverride.mutate(
                              { account_id: a.id, hidden: false },
                              { onError, onSuccess: () => toast.success(`${a.name} added`) },
                            )
                          }
                        >
                          <span className="truncate">{a.name}</span>
                          {a.code && (
                            <span className="ml-auto pl-2 text-[11px] text-muted-foreground">
                              {a.code}
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </>
                )}
                <DropdownMenuSeparator />
                <p className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
                  Tip: right-click any line to insert directly above or below it, rename it,
                  highlight it or add a note.
                </p>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {editableSheet && hiddenAccounts.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <EyeOff className="h-3.5 w-3.5" /> Hidden ({hiddenAccounts.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Click to show again</DropdownMenuLabel>
                {hiddenAccounts.map((h) => (
                  <DropdownMenuItem
                    key={h.rowId}
                    onSelect={() =>
                      setOverride.mutate({ account_id: h.accountId, hidden: false }, { onError })
                    }
                  >
                    {h.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
        </div>
      </div>

      <ProjectionsGrid
        sheet={sheet}
        rows={rows}
        model={pm.model}
        visibleMonths={pm.visibleMonths}
        settings={pm.settings}
        styles={pm.styles}
        zoom={zoom}
        columnMode={columnMode}
        density={density}
        viewMode={viewMode}
        height={height}
        onEdit={pm.applyEdits}
        onSetStyle={(input) => setStyle.mutate(input, { onError })}
        actions={actions}
        onGridReady={(api) => {
          apiRef.current = api;
        }}
        collapsedSections={collapsedSections}
        onToggleSection={toggleSection}
      />

      <Legend sheet={sheet} />
    </div>
  );
}

function Legend({ sheet }: { sheet: SheetId }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <i className="inline-block h-3 w-3 rounded-sm border border-border bg-white" /> Plan — type
        to edit (any month)
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="inline-block h-3 w-3 rounded-sm border border-border bg-[hsl(220_13%_91%/0.5)]" />
        Actual — from QuickBooks, never typed
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="inline-block h-3 w-3 rounded-sm bg-[hsl(12_75%_70%/0.5)]" /> Unfavourable
        variance / cash below floor
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="inline-block h-3 w-3 rounded-sm bg-[hsl(41_90%_70%/0.5)]" /> Watch
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="inline-block h-3 w-3 rounded-sm bg-[hsl(124_40%_60%/0.35)]" /> Ahead of plan
      </span>
      {sheet === 'main' && (
        <span>
          Cash section: + means cash in, − means cash out. Ending bank balance actual = QuickBooks
          bank accounts.
        </span>
      )}
      {sheet === 'balance-sheet' && (
        <span>Balances are derived from the Main sheet movements; edit them there.</span>
      )}
      <span>
        Ctrl+Z undo · Ctrl+V paste from a spreadsheet · Delete clears · right-click for more
      </span>
    </div>
  );
}
